// redux/reducer.ts
import {
  KycStatuses,
  P2PAccess,
} from "@services/models/Profile/GetProfileModel";
import secureLocalStorage from "react-secure-storage";
import {
  ADD_HISTORY,
  ADD_MERCHANT,
  RESET_STATE,
  SET_ERROR,
  SET_HAS_MERCHANT,
  SET_IS_DARK,
  SET_IS_PERSONAL,
  SET_PROFILE_LOADED,
  SET_PROFILE_MERCHANT_DATA,
  SET_USER,
  SET_WEBSOCKET_LOADED,
} from "./types";

export interface MerchantDetail {
  userId: number;
  merchantId: number;
  merchantURL: string | null;
  merchantName: string;
  alias: string | null;
  categoryName: string | null;
  categoryType: string | null;
  merchantUUID: string;
  active: boolean;
  storeName: string;
  profileImage: string;
  businessMode: string;
  country: string;
  createdAt: string;
  makerP2pRampFiat: number | null;
  makerP2pRampCrypto: number | null;
  allowKYCBypass: number | null;
}

export interface MerchantBalance {
  merchantId: number;
  balanceInDollars: number;
}

export interface ProfileMerchantData {
  userId: number;
  kycStatuses: KycStatuses;
  username: string;
  emailId: string;
}

export interface AppState {
  isPersonal: boolean;
  isDark: boolean;
  user: {
    userId: number | null;
    emailId: string;
    whatsappNumber: string;
    countryCode: string;
    userUUID: string;
    profileImage: string;
    firstName: string;
    currentMerchantId: number | null;
    merchantDetails: MerchantDetail[];
    personalBalance: number;
    merchantBalances: MerchantBalance[];
    p2pAccess: P2PAccess;
    kycStatuses: KycStatuses;
    createdAt: string;
    intercomUserHash: string;
  };
  profileMerchantData: ProfileMerchantData | null;
  hasMerchant: boolean;
  websocketLoaded: boolean;
  error: boolean;
  history: string[];
  addMerchant: boolean;
}

// Retrieve and parse boolean values and user data from secureLocalStorage
const storedIsDark = secureLocalStorage.getItem("isDark");
const isDarkState = storedIsDark === "true";

const storedHasMerchant = secureLocalStorage.getItem("hasMerchant");
const hasMerchantState = storedHasMerchant === "true";

const storedUser = secureLocalStorage.getItem("user");
const parsedUser = storedUser ? JSON.parse(storedUser as string) : null;

const storedCurrentMerchantId = secureLocalStorage.getItem("currentMerchantId");
const currentMerchantId = storedCurrentMerchantId
  ? parseInt(storedCurrentMerchantId as string, 10)
  : -1;

const initialState: AppState = {
  isPersonal: currentMerchantId === -1,
  isDark: isDarkState !== null ? isDarkState : false,
  user: parsedUser || {
    userId: null,
    emailId: "",
    whatsappNumber: "",
    countryCode: "",
    userUUID: "",
    profileImage: "",
    firstName: "",
    currentMerchantId: currentMerchantId,
    merchantDetails: [],
    personalBalance: 0,
    merchantBalances: [],
    p2pAccess: {
      makerFiat: 0,
      takerFiat: 0,
      feeProfile: 0,
      makerCrypto: 0,
      takerCrypto: 0,
      makerP2pRampFiat: 0,
      takerP2pRampFiat: 0,
      makerP2pRampCrypto: 0,
      takerP2pRampCrypto: 0,
    },
    kycStatuses: {
      idStatus: 0,
      addressStatus: 0,
    },
    createdAt: "",
    intercomUserHash: "",
  },
  profileMerchantData: null,
  websocketLoaded: false,
  hasMerchant: hasMerchantState !== null ? hasMerchantState : false,
  error: false,
  history: ["/"],
  addMerchant: false,
};

type AppAction =
  | { type: typeof SET_IS_PERSONAL; payload: boolean }
  | { type: typeof SET_IS_DARK; payload: boolean }
  | { type: typeof SET_USER; payload: Partial<AppState["user"]> }
  | { type: typeof SET_PROFILE_LOADED; payload: boolean }
  | { type: typeof SET_HAS_MERCHANT; payload: boolean }
  | { type: typeof SET_WEBSOCKET_LOADED; payload: boolean }
  | { type: typeof SET_ERROR; payload: boolean }
  | { type: typeof ADD_HISTORY; payload: string }
  | { type: typeof RESET_STATE }
  | { type: typeof ADD_MERCHANT; payload: boolean }
  | { type: typeof SET_PROFILE_MERCHANT_DATA; payload: ProfileMerchantData };

const appReducer = (state = initialState, action: AppAction): AppState => {
  switch (action.type) {
    case SET_PROFILE_MERCHANT_DATA:
      return {
        ...state,
        profileMerchantData: action.payload,
      };

    case SET_IS_PERSONAL:
      return {
        ...state,
        isPersonal: action.payload,
      };
    case SET_IS_DARK:
      secureLocalStorage.setItem("isDark", action.payload.toString());
      return {
        ...state,
        isDark: action.payload,
      };
    case SET_USER:
      const updatedUser = {
        ...state.user,
        ...action.payload,
        p2pAccess: action.payload.p2pAccess || state.user.p2pAccess,
        kycStatuses: action.payload.kycStatuses || state.user.kycStatuses,
        personalBalance:
          action.payload.personalBalance !== undefined
            ? action.payload.personalBalance
            : state.user.personalBalance,
        merchantBalances:
          action.payload.merchantBalances !== undefined
            ? action.payload.merchantBalances
            : state.user.merchantBalances,
        currentMerchantId:
          action.payload.currentMerchantId !== undefined
            ? action.payload.currentMerchantId
            : state.user.currentMerchantId,
      };

      // Store the updated user object and currentMerchantId in secureLocalStorage
      secureLocalStorage.setItem("user", JSON.stringify(updatedUser));
      secureLocalStorage.setItem(
        "currentMerchantId",
        updatedUser.currentMerchantId?.toString() || "-1"
      );

      return {
        ...state,
        user: updatedUser,
      };
    case SET_WEBSOCKET_LOADED:
      return {
        ...state,
        websocketLoaded: action.payload,
      };
    case SET_HAS_MERCHANT:
      secureLocalStorage.setItem("hasMerchant", action.payload.toString());
      return {
        ...state,
        hasMerchant: action.payload,
      };
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case ADD_HISTORY:
      return {
        ...state,
        history: [...state.history, action.payload],
      };
    case RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

export default appReducer;
